import React, { useState, useEffect, useCallback } from 'react';
import { theme, btnPrimary, btnSecondary } from '../theme';
// v0.61.0 — lucide glyphs. FileText/Image drive the per-row mime badge;
// DownloadCloud is the header + row-action glyph; RefreshCw is retry.
import { DownloadCloud, FileText, Image as ImageIcon, RefreshCw } from 'lucide-react';
import { apiGet, apiSend } from '../api';

/**
 * ImportFromInSiteModal — v0.61.0.
 *
 * Lets the user IMPORT a plan already saved in InSite, instead of uploading
 * a file. Opens from PlanScreen's picker row.
 *
 * 2026-11-01 — the JOB's files too. Brett: "in droppin you are not linking to
 * files on the actual job, you only have location files ... it sits in files".
 * With a report open from an InSite job or quote, InSite also lists that job's
 * drawings and files (PDFs and pictures), shown first under "On this job".
 *
 *   1. GET /api/location-plans?projectId=<id>[&reportId=<id>]
 *        -> { ok, plans:[{ id, name, number, mime, url, updatedAt,
 *                          source: 'job'|'location' }],
 *             pending?, message? }
 *   2. Click a plan -> POST /api/import-location-plan { projectId, plan }
 *        -> { ok, id, name }   (dedupes server-side)
 *   3. On success: onImported() (refreshes PlanScreen's drawing list) + close.
 *
 * DARK MODE: every colour is a theme token (theme.surface/bg/text/textLight/
 * brand/border/danger). No hex literals, no 'white'. The scrim keeps the
 * app-standard translucent-black rgba(15,23,42,0.5).
 *
 * Props:
 *   projectId    number|string          — DropPin Projects.Id
 *   onImported   (id, name) => void      — called after a successful import
 *   onClose      () => void
 */
export default function ImportFromInSiteModal({ projectId, reportId, onImported, onClose }) {
  const [status, setStatus]         = useState('loading'); // loading|list|empty|pending|error
  const [plans, setPlans]           = useState([]);
  const [message, setMessage]       = useState('');        // server pending message
  const [err, setErr]               = useState('');        // load error
  const [importingId, setImportingId] = useState(null);
  const [rowErr, setRowErr]         = useState({});        // { [planId]: 'message' }

  const load = useCallback(async () => {
    setStatus('loading'); setErr(''); setMessage(''); setRowErr({});
    try {
      const forReport = (reportId != null && reportId !== '') ? `&reportId=${encodeURIComponent(reportId)}` : '';
      const res = await apiGet(`/api/location-plans?projectId=${encodeURIComponent(projectId)}${forReport}`);
      const list = Array.isArray(res && res.plans) ? res.plans : [];
      if (res && res.pending) {
        setMessage(res.message || 'Plans aren’t synced yet.');
        setStatus('pending');
        return;
      }
      if (!list.length) { setStatus('empty'); return; }
      setPlans(list);
      setStatus('list');
    } catch (e) {
      setErr(e.message || String(e));
      setStatus('error');
    }
  }, [projectId, reportId]);

  useEffect(() => { load(); }, [load]);

  async function handleImport(p) {
    if (importingId) return;
    setImportingId(p.id);
    setRowErr((m) => { const n = { ...m }; delete n[p.id]; return n; });
    try {
      const res = await apiSend('POST', '/api/import-location-plan', {
        projectId: Number(projectId),
        reportId: (reportId != null && reportId !== '') ? Number(reportId) : undefined,
        // v0.141.0 — carry the sheet's SCALE across. InSite ships it on the
        // listing (Drawings.CalibrationJson) when someone has calibrated the
        // sheet for a take-off; the importer turns it into DropPin's own
        // calibration so an imported plan measures in m² rather than px².
        // 2026-10-02 — the id names the plan; the server fetches InSpec's own
        // copy of that entry, never the url sent here.
        plan: { id: p.id, url: p.url, name: p.name, number: p.number, mime: p.mime,
                calibrationJson: p.calibrationJson || null },
      });
      const newId = res && (res.id ?? res.Id);
      onImported && onImported(newId, p.name);
      onClose && onClose();
    } catch (e) {
      setRowErr((m) => ({ ...m, [p.id]: e.message || String(e) }));
      setImportingId(null);
    }
  }

  // ---- shared token-based style objects (see design spec) ----
  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 100, padding: 16,
  };
  const panel = {
    background: theme.surface, color: theme.text,
    width: 'min(560px, 100%)', maxHeight: '85vh',
    borderRadius: 12, display: 'flex', flexDirection: 'column',
    overflow: 'hidden', border: `1px solid ${theme.border}`,
    boxShadow: '0 12px 32px rgba(0,0,0,0.28)',
  };
  const header = {
    padding: '14px 18px', borderBottom: `1px solid ${theme.border}`,
    display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12,
  };
  const headerTitle = {
    margin: 0, fontSize: 17, fontWeight: 700, color: theme.text,
    display: 'flex', alignItems: 'center', gap: 8,
  };
  const headerSub = { fontSize: 12, color: theme.textLight, whiteSpace: 'nowrap' };
  const body = {
    overflowY: 'auto', flex: 1, padding: '12px 14px 14px',
    display: 'flex', flexDirection: 'column', gap: 6,
  };
  const footer = {
    padding: '10px 18px', borderTop: `1px solid ${theme.border}`,
    display: 'flex', justifyContent: 'flex-end',
  };
  const centreState = {
    padding: '32px 20px', textAlign: 'center', color: theme.textLight, fontSize: 13,
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
  };
  const noteBox = {
    marginTop: 4, padding: '10px 12px', borderRadius: 8,
    border: `1px solid ${theme.border}`, background: theme.bg,
    color: theme.text, fontSize: 12.5, maxWidth: 380,
  };
  const errorBox = {
    margin: '4px 0', padding: '12px 14px', borderRadius: 8,
    border: `1px solid ${theme.danger}`, background: theme.bg,
    color: theme.danger, fontSize: 13,
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, textAlign: 'center',
  };
  const rowBase = {
    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
    border: `1px solid ${theme.border}`, borderRadius: 8, background: theme.surface,
  };
  const mimeChip = {
    width: 34, height: 34, flexShrink: 0, borderRadius: 6,
    background: theme.bg, border: `1px solid ${theme.border}`,
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.textLight,
  };
  const rowTextWrap = { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 };
  const planName = {
    fontSize: 13, fontWeight: 600, color: theme.text,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  };
  const planMeta = {
    fontSize: 11, color: theme.textLight, display: 'flex', alignItems: 'center', gap: 8,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  };
  const mimeBadge = {
    fontSize: 10, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase',
    color: theme.textLight, border: `1px solid ${theme.border}`, borderRadius: 4,
    padding: '1px 6px', flexShrink: 0,
  };
  const rowImportBtn = {
    ...btnPrimary, fontSize: 12, padding: '6px 12px',
    display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0, whiteSpace: 'nowrap',
  };
  const groupHeading = {
    margin: '6px 2px 0', fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
    textTransform: 'uppercase', color: theme.textLight,
  };
  const rowError = {
    margin: '-2px 0 4px', padding: '6px 10px', borderRadius: 6,
    border: `1px solid ${theme.danger}`, background: theme.bg,
    color: theme.danger, fontSize: 11.5,
  };

  function Spinner({ size = 14 }) {
    return (
      <span style={{
        display: 'inline-block', width: size, height: size,
        border: `2px solid ${theme.border}`, borderTopColor: theme.brand,
        borderRadius: '50%', animation: 'inspec-spin 0.7s linear infinite',
        flexShrink: 0, verticalAlign: 'middle',
      }} />
    );
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={panel} onClick={(e) => e.stopPropagation()}>
        {/* keyframe for the spinner (same name PlanScreen uses; harmless if dup) */}
        <style>{`@keyframes inspec-spin { to { transform: rotate(360deg); } }`}</style>

        <div style={header}>
          <h3 style={headerTitle}>
            <DownloadCloud size={18} style={{ color: theme.brand }} />
            Import a plan from InSite
          </h3>
          {status === 'list' && plans.length > 0 && (
            <span style={headerSub}>{plans.length} available</span>
          )}
        </div>

        <div style={body}>
          {status === 'loading' && (
            <div style={centreState}>
              <Spinner size={22} />
              <div>Loading plans from InSite…</div>
            </div>
          )}

          {status === 'empty' && (
            <div style={centreState}>
              <FileText size={26} style={{ color: theme.textLight, opacity: 0.6 }} />
              <div>No plans or files in InSite for this location or its job yet.</div>
            </div>
          )}

          {status === 'pending' && (
            <div style={centreState}>
              <RefreshCw size={24} style={{ color: theme.brand }} />
              <div style={{ fontWeight: 600, color: theme.text }}>Plans aren’t synced yet</div>
              <div style={noteBox}>
                {message || 'Re-open Locations in InSite to sync, then try again.'}
              </div>
              <button
                onClick={load}
                style={{ ...btnSecondary, fontSize: 13, padding: '6px 12px', display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <RefreshCw size={13} /> Try again
              </button>
            </div>
          )}

          {status === 'error' && (
            <div style={errorBox}>
              <div style={{ fontWeight: 600 }}>Couldn’t load plans from InSite</div>
              <div style={{ color: theme.text }}>{err}</div>
              <button onClick={load} style={{ ...btnSecondary, fontSize: 13, padding: '6px 12px' }}>Try again</button>
            </div>
          )}

          {status === 'list' && [
            { key: 'job', title: 'On this job', rows: plans.filter((p) => p.source === 'job') },
            { key: 'location', title: 'On this location', rows: plans.filter((p) => p.source !== 'job') },
          ].filter((g) => g.rows.length).map((g) => (
            <React.Fragment key={g.key}>
              <div style={groupHeading} data-group={g.key}>{g.title}</div>
              {g.rows.map((p) => {
            const isImg = /image/i.test(p.mime || '');
            const typeLabel = isImg ? 'Image' : /pdf/i.test(p.mime || '') ? 'PDF' : (p.mime || 'File');
            const busy = importingId === p.id;
            const thisErr = rowErr[p.id];
            return (
              <React.Fragment key={p.id != null ? p.id : p.name}>
                <div style={busy ? { ...rowBase, opacity: 0.6 } : rowBase}>
                  <div style={mimeChip}>
                    {isImg ? <ImageIcon size={16} /> : <FileText size={16} />}
                  </div>
                  <div style={rowTextWrap}>
                    <div style={planName} title={p.name}>{p.name || '(untitled plan)'}</div>
                    <div style={planMeta}>
                      <span style={mimeBadge}>{typeLabel}</span>
                      {p.number ? <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>No. {p.number}</span> : null}
                    </div>
                  </div>
                  <button
                    onClick={() => handleImport(p)}
                    disabled={busy || !!importingId}
                    style={busy ? { ...rowImportBtn, opacity: 0.7, cursor: 'default' } : rowImportBtn}
                    title="Import this plan into the report"
                  >
                    {busy
                      ? (<><Spinner size={12} /> Importing…</>)
                      : (<><DownloadCloud size={13} /> Import</>)}
                  </button>
                </div>
                {thisErr && <div style={rowError}>{thisErr}</div>}
              </React.Fragment>
            );
              })}
            </React.Fragment>
          ))}
        </div>

        <div style={footer}>
          <button onClick={onClose} style={btnSecondary}>Close</button>
        </div>
      </div>
    </div>
  );
}
